import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, DestroyRef, ElementRef, HostBinding, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { WINDOW } from '@frontend/vanilla/core';
import { fromEvent } from 'rxjs';

import { Promotion, TrackingSource } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { ComponentProxy } from '../deferred/component-loader.service';
import { ComponentProxyDirective } from '../deferred/component-proxy.directive';
import { HighLightsMarqueeBinding, HighlightsMarqueeComponentLoaderService } from '../highlights-marquee/highlights-marquee-component-loader.service';

@Component({
    selector: 'ms-crm-promotion-container',
    templateUrl: 'crm-promotion-container.component.html',
    imports: [CommonModule, CrmPromotionModule, ComponentProxyDirective],
})
export class CrmPromotionContainerComponent implements OnChanges, AfterContentInit {
    @HostBinding('class.height-increase-allowed') heightIncreaseAllowedClass: boolean;
    @HostBinding('class.show-card-shadow') showCardShadowClass: boolean;
    @HostBinding('class.single') singleBannerClass: boolean;
    @HostBinding('class.double') doubleBannerClass: boolean;
    @HostBinding('class.multi') multiBannerClass: boolean;

    @Input() heightIncreaseAllowed?: boolean;
    @Input() showCardShadow?: boolean;
    @Input() promotions: Promotion[] = [];
    @Input() trackingSource = TrackingSource.Teaser;

    private doubleBannerMarqueeBreakpointPx = 565;
    scrollingSpeed = { default: 'moderate' };
    marqueeComponent: ComponentProxy<HighLightsMarqueeBinding>;
    showMarquee = false;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        private elementRef: ElementRef,
        private marqueeLoader: HighlightsMarqueeComponentLoaderService,
    ) {
        this.marqueeComponent = this.marqueeLoader.getHighlightsMarqueeComponent();
        this.marqueeComponent.update({ scrollingSpeed: this.scrollingSpeed });
    }

    ngOnChanges(changes: ISimpleChanges<CrmPromotionContainerComponent>): void {
        const change = changes.promotions;

        if (!change) {
            return;
        }

        this.heightIncreaseAllowedClass = !!this.heightIncreaseAllowed;
        this.showCardShadowClass = !!this.showCardShadow;
        this.singleBannerClass = this.promotions.length === 1;
        this.doubleBannerClass = this.promotions.length === 2;
        this.multiBannerClass = this.promotions.length > 2;
        this.showMarquee = this.promotions.length >= 2;
        this.marqueeComponent.update({
            promotionTiles: this.promotions,
        });
        this.setClasses();
    }

    trackPromoBy(index: number, item: Promotion): string {
        return item.id;
    }

    ngAfterContentInit(): void {
        fromEvent(this._window, 'resize')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setClasses());
    }

    private setClasses(): void {
        if (this.promotions.length !== 2) {
            return;
        }

        this.doubleBannerClass = this.elementRef.nativeElement.offsetWidth > this.doubleBannerMarqueeBreakpointPx;
    }
}
